// SPDX-License-Identifier: AGPL-3.0-or-later

package kairos

sealed interface KairosMarkdownBlock {
    data class Heading(val level: Int, val content: String) : KairosMarkdownBlock

    data class Paragraph(val content: String) : KairosMarkdownBlock

    data class BulletList(val items: List<String>) : KairosMarkdownBlock

    data class OrderedList(val items: List<String>) : KairosMarkdownBlock

    data class Quote(val content: String) : KairosMarkdownBlock

    data class CodeFence(val code: String) : KairosMarkdownBlock
}

sealed interface KairosMarkdownInline {
    data class Text(val value: String) : KairosMarkdownInline

    data class Bold(val children: List<KairosMarkdownInline>) : KairosMarkdownInline

    data class Italic(val children: List<KairosMarkdownInline>) : KairosMarkdownInline

    data class Code(val value: String) : KairosMarkdownInline

    data class Link(val label: List<KairosMarkdownInline>, val url: String) : KairosMarkdownInline
}

fun parseKairosMarkdown(source: String): List<KairosMarkdownBlock> {
    val normalized = source.replace("\r\n", "\n")
    if (normalized.isBlank()) return emptyList()
    val lines = normalized.split('\n')
    val blocks = mutableListOf<KairosMarkdownBlock>()
    var index = 0
    while (index < lines.size) {
        val rawLine = lines[index]
        val trimmed = rawLine.trim()
        when {
            trimmed.isBlank() -> {
                index++
            }

            trimmed.startsWith("```") -> {
                val code = mutableListOf<String>()
                index++
                while (index < lines.size && !lines[index].trim().startsWith("```")) {
                    code += lines[index]
                    index++
                }
                if (index < lines.size && lines[index].trim().startsWith("```")) {
                    index++
                }
                blocks += KairosMarkdownBlock.CodeFence(code.joinToString("\n"))
            }

            headingLevel(trimmed) > 0 -> {
                val level = headingLevel(trimmed)
                blocks += KairosMarkdownBlock.Heading(level, trimmed.drop(level).trim())
                index++
            }

            isBulletItem(trimmed) -> {
                val items = mutableListOf<String>()
                while (index < lines.size && isBulletItem(lines[index].trim())) {
                    items += lines[index].trim().drop(2).trim()
                    index++
                }
                blocks += KairosMarkdownBlock.BulletList(items)
            }

            orderedItemPrefix(trimmed) != null -> {
                val items = mutableListOf<String>()
                while (index < lines.size) {
                    val prefix = orderedItemPrefix(lines[index].trim()) ?: break
                    items += lines[index].trim().drop(prefix).trim()
                    index++
                }
                blocks += KairosMarkdownBlock.OrderedList(items)
            }

            trimmed.startsWith(">") -> {
                val linesInQuote = mutableListOf<String>()
                while (index < lines.size && lines[index].trim().startsWith(">")) {
                    linesInQuote += lines[index].trim().removePrefix(">").trim()
                    index++
                }
                blocks += KairosMarkdownBlock.Quote(linesInQuote.joinToString("\n"))
            }

            else -> {
                val paragraph = mutableListOf<String>()
                while (
                    index < lines.size &&
                        lines[index].trim().isNotBlank() &&
                        !lines[index].trim().startsWith("```") &&
                        headingLevel(lines[index].trim()) == 0 &&
                        !isBulletItem(lines[index].trim()) &&
                        orderedItemPrefix(lines[index].trim()) == null &&
                        !lines[index].trim().startsWith(">")
                ) {
                    paragraph += lines[index].trim()
                    index++
                }
                blocks += KairosMarkdownBlock.Paragraph(paragraph.joinToString(" "))
            }
        }
    }
    return blocks
}

fun parseKairosMarkdownInline(source: String): List<KairosMarkdownInline> {
    if (source.isEmpty()) return emptyList()
    val fragments = mutableListOf<KairosMarkdownInline>()
    var index = 0
    while (index < source.length) {
        when {
            source.startsWith("**", index) -> {
                val end = source.indexOf("**", index + 2)
                if (end > index + 2) {
                    fragments +=
                        KairosMarkdownInline.Bold(
                            parseKairosMarkdownInline(source.substring(index + 2, end))
                        )
                    index = end + 2
                } else {
                    fragments += KairosMarkdownInline.Text("**")
                    index += 2
                }
            }

            source[index] == '`' -> {
                val end = source.indexOf('`', index + 1)
                if (end > index + 1) {
                    fragments += KairosMarkdownInline.Code(source.substring(index + 1, end))
                    index = end + 1
                } else {
                    fragments += KairosMarkdownInline.Text("`")
                    index++
                }
            }

            source[index] == '[' -> {
                val labelEnd = source.indexOf(']', index + 1)
                val hasUrl =
                    labelEnd != -1 && labelEnd + 1 < source.length && source[labelEnd + 1] == '('
                val urlEnd = if (hasUrl) source.indexOf(')', labelEnd + 2) else -1
                if (labelEnd > index + 1 && urlEnd > labelEnd + 2) {
                    fragments +=
                        KairosMarkdownInline.Link(
                            label =
                                parseKairosMarkdownInline(source.substring(index + 1, labelEnd)),
                            url = source.substring(labelEnd + 2, urlEnd),
                        )
                    index = urlEnd + 1
                } else {
                    fragments += KairosMarkdownInline.Text(source[index].toString())
                    index++
                }
            }

            source[index] == '*' -> {
                val end = source.indexOf('*', index + 1)
                if (end > index + 1) {
                    fragments +=
                        KairosMarkdownInline.Italic(
                            parseKairosMarkdownInline(source.substring(index + 1, end))
                        )
                    index = end + 1
                } else {
                    fragments += KairosMarkdownInline.Text("*")
                    index++
                }
            }

            else -> {
                val nextSpecial =
                    listOf(
                            source.indexOf("**", index).takeIf { it >= 0 },
                            source.indexOf('`', index).takeIf { it >= 0 },
                            source.indexOf('[', index).takeIf { it >= 0 },
                            source.indexOf('*', index).takeIf { it >= 0 },
                        )
                        .filterNotNull()
                        .minOrNull() ?: source.length
                fragments += KairosMarkdownInline.Text(source.substring(index, nextSpecial))
                index = nextSpecial
            }
        }
    }
    return mergeAdjacentText(fragments)
}

private fun mergeAdjacentText(fragments: List<KairosMarkdownInline>): List<KairosMarkdownInline> {
    if (fragments.isEmpty()) return fragments
    val merged = mutableListOf<KairosMarkdownInline>()
    fragments.forEach { fragment ->
        val previous = merged.lastOrNull()
        if (previous is KairosMarkdownInline.Text && fragment is KairosMarkdownInline.Text) {
            merged[merged.lastIndex] = KairosMarkdownInline.Text(previous.value + fragment.value)
        } else {
            merged += fragment
        }
    }
    return merged
}

private fun headingLevel(line: String): Int {
    var count = 0
    while (count < line.length && count < 6 && line[count] == '#') {
        count++
    }
    return if (count > 0 && line.getOrNull(count) == ' ') count else 0
}

private fun isBulletItem(line: String): Boolean =
    line.length > 2 && (line.startsWith("- ") || line.startsWith("* ") || line.startsWith("+ "))

private fun orderedItemPrefix(line: String): Int? {
    var index = 0
    while (index < line.length && line[index].isDigit()) {
        index++
    }
    return when {
        index == 0 -> null
        index + 1 >= line.length -> null
        (line[index] == '.' || line[index] == ')') && line[index + 1] == ' ' -> index + 2
        else -> null
    }
}
